import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Term } from '../../components/admin/AdminGlossary';

interface AuditEntry {
  id: string; action: string; entity_type: string; entity_id: string | null;
  old_value: unknown; new_value: unknown; created_at: string;
  profiles: { email: string | null } | null;
}

interface FeatureStat { feature: string; icon: string; total: number; last30: number; deleted: number }

interface DeletedAnalysis {
  id: string; location_name: string; country: string; flag: string;
  overall_score: number; created_at: string; deleted_at: string; user_id: string;
}

export function AdminAuditPage() {
  const [entries,    setEntries]    = useState<AuditEntry[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState<string | null>(null);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom,  setFilterFrom]   = useState('');
  const [filterTo,    setFilterTo]     = useState('');

  const [featureStats,    setFeatureStats]    = useState<FeatureStat[]>([]);
  const [deletedAnalyses, setDeletedAnalyses] = useState<DeletedAnalysis[]>([]);
  const [statsLoading,    setStatsLoading]    = useState(true);

  useEffect(() => { load(); }, [filterEmail, filterAction, filterFrom, filterTo]);
  useEffect(() => { loadStats(); }, []);

  async function loadStats() {
    setStatsLoading(true);
    const since30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const [synRes, astroRes, qRes, astromatchRes, deletedRes] = await Promise.all([
      supabase.from('synastry_readings').select('id, created_at', { count: 'exact' }),
      supabase.from('astrocartografia_analyses').select('id, created_at, deleted_at', { count: 'exact' }),
      supabase.from('astrological_questions').select('id, created_at', { count: 'exact' }),
      supabase.from('astromatch_readings').select('id, created_at', { count: 'exact' }),
      supabase
        .from('astrocartografia_analyses')
        .select('id, location_name, country, flag, overall_score, created_at, deleted_at, user_id')
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false })
        .limit(50),
    ]);

    const synData      = synRes.data ?? [];
    const astroData    = astroRes.data ?? [];
    const qData        = qRes.data ?? [];
    const matchData    = astromatchRes.data ?? [];

    const count30 = (arr: { created_at: string }[]) =>
      arr.filter(r => r.created_at >= since30).length;

    const astroDeleted = astroData.filter(r => (r as { deleted_at: string | null }).deleted_at);

    setFeatureStats([
      { feature: 'Sinastria',         icon: '💫', total: synData.length,   last30: count30(synData),   deleted: 0 },
      { feature: 'Astrocartografia',  icon: '🌍', total: astroData.length, last30: count30(astroData), deleted: astroDeleted.length },
      { feature: 'Perguntas',         icon: '🔮', total: qData.length,     last30: count30(qData),     deleted: 0 },
      { feature: 'AstroMatch',        icon: '💘', total: matchData.length, last30: count30(matchData), deleted: 0 },
    ]);

    setDeletedAnalyses((deletedRes.data ?? []) as unknown as DeletedAnalysis[]);
    setStatsLoading(false);
  }

  async function load() {
    setLoading(true);
    let q = supabase
      .from('admin_audit_log')
      .select('id, action, entity_type, entity_id, old_value, new_value, created_at, profiles:admin_id(email)')
      .order('created_at', { ascending: false })
      .limit(100);

    if (filterAction) q = q.ilike('action', `%${filterAction}%`);
    if (filterFrom)   q = q.gte('created_at', filterFrom + 'T00:00:00Z');
    if (filterTo)     q = q.lte('created_at', filterTo + 'T23:59:59Z');

    const { data } = await q;
    let results = (data ?? []) as unknown as AuditEntry[];

    if (filterEmail) {
      results = results.filter(e => e.profiles?.email?.toLowerCase().includes(filterEmail.toLowerCase()));
    }

    setEntries(results);
    setLoading(false);
  }

  function exportCsv() {
    const headers = ['timestamp', 'admin', 'action', 'entity_type', 'entity_id'];
    const rows = entries.map(e => [
      e.created_at, e.profiles?.email ?? '', e.action, e.entity_type, e.entity_id ?? '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `audit_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const ACTION_LABELS: Record<string, string> = {
    update_price: 'Atualizou preço',
    create_coupon: 'Criou cupom',
    pause_coupon: 'Pausou cupom',
    activate_coupon: 'Ativou cupom',
    delete_coupon: 'Excluiu cupom',
    grant_credits: 'Concedeu créditos',
    issue_refund: 'Emitiu reembolso',
    delete_account: 'Excluiu conta de usuário',
    reset_all_prices: 'Resetou todos os preços',
  };

  return (
    <div>
      <AdminPageHeader
        title="Auditoria"
        objective="Registro completo de todas as ações administrativas: quem fez, o que fez, quando fez e o que mudou. Use para investigar problemas, garantir conformidade legal e rastrear responsabilidades. Dados mantidos por pelo menos 5 anos."
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={exportCsv}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      {/* Feature usage stats */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
          Uso por funcionalidade
        </p>
        {statsLoading ? (
          <div className="shimmer" style={{ height: 60, borderRadius: 8 }} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {featureStats.map(s => (
              <div key={s.feature} style={{ padding: '0.75rem', background: 'var(--bg-base)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                <p style={{ fontSize: '1.25rem', margin: '0 0 0.25rem' }}>{s.icon}</p>
                <p style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{s.feature}</p>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
                  Total: <strong style={{ color: 'var(--text-secondary)' }}>{s.total}</strong>
                  {' · '}30d: <strong style={{ color: '#4ade80' }}>{s.last30}</strong>
                  {s.deleted > 0 && <>{' · '}excluídos: <strong style={{ color: '#f87171' }}>{s.deleted}</strong></>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deleted astrocartografia analyses */}
      {!statsLoading && deletedAnalyses.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
            🗑 Localizações excluídas pelos usuários
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Localização</th>
                  <th>Score</th>
                  <th>Criada em</th>
                  <th>Excluída em</th>
                  <th>User ID</th>
                </tr>
              </thead>
              <tbody>
                {deletedAnalyses.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      {d.flag} {d.location_name}
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginLeft: '0.4rem' }}>{d.country}</span>
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{d.overall_score}/100</td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(d.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: '#fca5a5', whiteSpace: 'nowrap' }}>
                      {new Date(d.deleted_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {d.user_id.slice(0, 8)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admin action log */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: 0 }}>
          Log de ações administrativas, conformidade <Term k="LGPD">LGPD</Term>
        </p>
        <span style={{ fontSize: 'var(--text-caption)', color: '#94a3b8', padding: '0.25rem 0.625rem', backgroundColor: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 'var(--radius-sm)' }}>
          🔒 Registros de auditoria são imutáveis, não podem ser editados ou excluídos.
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input type="text" className="form-input" value={filterEmail} onChange={e => setFilterEmail(e.target.value)} placeholder="Admin e-mail" style={{ maxWidth: '200px' }} />
        <input type="text" className="form-input" value={filterAction} onChange={e => setFilterAction(e.target.value)} placeholder="Tipo de ação" style={{ maxWidth: '180px' }} />
        <input type="date" className="form-input" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ maxWidth: '160px' }} />
        <input type="date" className="form-input" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={{ maxWidth: '160px' }} />
        <button className="btn btn-ghost btn-sm" onClick={() => { setFilterEmail(''); setFilterAction(''); setFilterFrom(''); setFilterTo(''); }}>
          Limpar
        </button>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Quando</th>
                <th>Admin</th>
                <th>Ação</th>
                <th>Entidade</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <>
                  <tr key={e.id}>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(e.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                      {e.profiles?.email
                        ? e.profiles.email
                        : <span
                            style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)' }}
                            title="ID do administrador, nome completo disponível após sincronização com perfis"
                          >
                            {e.entity_id?.slice(0, 8) ?? '-'}…
                          </span>
                      }
                    </td>
                    <td style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      {ACTION_LABELS[e.action] ?? e.action}
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {e.entity_type}/{e.entity_id?.slice(0, 12) ?? '-'}
                    </td>
                    <td>
                      {(!!e.old_value || !!e.new_value) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                          {expanded === e.id ? '▲ Fechar' : '▼ Ver'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr key={e.id + '-detail'}>
                      <td colSpan={5} style={{ padding: '0.5rem 1rem 1rem', backgroundColor: 'var(--bg-elevated)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>ANTES</p>
                            <pre style={{ fontSize: '11px', color: '#fca5a5', backgroundColor: 'var(--bg-base)', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '120px', margin: 0 }}>
                              {JSON.stringify(e.old_value, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>DEPOIS</p>
                            <pre style={{ fontSize: '11px', color: '#4ade80', backgroundColor: 'var(--bg-base)', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '120px', margin: 0 }}>
                              {JSON.stringify(e.new_value, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                  Nenhuma entrada encontrada
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
